import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { getPageUrl } from "@/lib/site";
import {
  Outlet,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { type ReactNode } from "react";
import { Toaster } from "@/components/ui/sonner";
import appCss from "../styles.css?url";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
head: () => ({
  meta: [
    { charSet: "utf-8" },
    { name: "viewport", content: "width=device-width, initial-scale=1" },
    { property: "og:site_name", content: "Kathmandu National Medical College" },
    { property: "og:type", content: "website" },
  ],
  scripts: [
    {
      src: "https://www.googletagmanager.com/gtag/js?id=G-7J0FQ5KNY8",
      async: true,
    },
    {
      children: `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-7J0FQ5KNY8');`,
    },
  ],
  links: [
    {
      rel: "icon",
      type: "image/png",
      href: getPageUrl("/files/favicon-32x32.png"),
    },
    { rel: "stylesheet", href: appCss },
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    {
      rel: "preconnect",
      href: "https://fonts.gstatic.com",
      crossOrigin: "anonymous",
    },
  ],
}),
  shellComponent: Shell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorView,
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <div id="root" className="min-h-screen bg-background font-sans antialiased">
          {children}
        </div>
        <Scripts />
      </body>
    </html>
  );
}

import * as frappeSDK from "frappe-react-sdk";

const SDK: Record<string, any> = (frappeSDK as any).default || frappeSDK;
const { FrappeContext, FrappeProvider: FP } = SDK;

function FrappeProvider({ children }: { children: ReactNode }) {
  const url = process.env.FRAPPE_URL;

  // Fetch the CSRF token after mount. frappe-js-sdk reads window.csrf_token on
  // every request, so children can render immediately
  useEffect(() => {
    fetch(`/api/method/opterp_web.api.csrf.get_csrf`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        (window as any).csrf_token = data.message.csrf_token;
      })
      .catch((e) => console.error(e));
  }, []);

  if (typeof window === "undefined") {
    const stub = { get: () => Promise.resolve({ message: [] }), post: () => Promise.resolve({ message: {} }), put: () => Promise.resolve({ message: {} }), delete: () => Promise.resolve({ message: {} }) };
    return <FrappeContext.Provider value={{ url, app: null as any, auth: stub, db: stub, call: stub, file: stub, socket: null, enableSocket: false }}>{children}</FrappeContext.Provider>;
  }
  return <FP url={url} enableSocket={false}>{children}</FP>;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <FrappeProvider>
        <Outlet />
        <Toaster richColors />
      </FrappeProvider>
    </QueryClientProvider>
  );
}

function NotFound() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <a
          href="/"
          className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90"
        >
          Go home
        </a>
      </div>
    </main>
  );
}

function ErrorView({ error }: ErrorComponentProps) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-6xl font-bold text-destructive">500</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Something went wrong</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {error?.message || "An unexpected error occurred. Please try again."}
        </p>
        <a
          href="/"
          className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90"
        >
          Go home
        </a>
      </div>
    </main>
  );
}
